// ============================================================================
// Summary → Driver performance → the drivers behind the figures. One row per
// DRIVER (not per shipment): when their vehicle arrived, whether that beat the
// morning cutoff, and how much of the load they carried missed its delivery SLA.
//
// TWO SHAPES, decided by the date filter. Over ONE day a driver has one arrival,
// so the table prints the clock time and its verdict. Over a RANGE they have one
// per morning, and a single cell cannot honestly stand for all of them — so those
// two columns collapse into a tally, "3 of 5 days". See `driver_performance.ts` for
// why the tally rather than an average.
//
// The mornings behind a tally open in a SECOND panel beside this one rather than in
// a hover: a fortnight's filter makes fourteen lines, and a tooltip that long runs
// off the screen, cannot be scrolled and disappears the moment you reach for it.
// A panel can be read at leisure and closed — and it leaves room to say, per day,
// what the driver was doing, which a tooltip never had.
//
// No fetch: the list is derived from the rows the page has already loaded and
// filtered, so its length always equals the total on the tile that opened it.
// ============================================================================
import { Drawer, Pagination, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useEffect, useMemo, useState } from "react";
import { fmtIST } from "../format";
import {
  ARRIVAL_CUTOFF_LABEL,
  ARRIVAL_DATA_AVAILABLE,
  ArrivalStatus,
  dayTally,
  DriverDay,
  DriverPerformance,
} from "./driver_performance";

interface Props {
  open: boolean;
  drivers: DriverPerformance[];
  // Whether the dispatch filter spans more than one day — which of the two shapes above to show.
  multiDay: boolean;
  onClose: () => void;
}

// Worded and coloured, never colour alone — the house rule for every status tag on these boards.
const STATUS_META: Record<ArrivalStatus, { color: string; label: string }> = {
  on_time: { color: "green", label: "On time" },
  late: { color: "red", label: "Late" },
};

// One morning per row — the panel behind a tally, so the figure can always be taken apart into the
// days it came from.
const dayColumns: ColumnsType<DriverDay> = [
  {
    title: "Day",
    key: "day",
    render: (_, x) => <span style={{ whiteSpace: "nowrap" }}>{fmtIST(x.arrivedAt, "ddd, D MMM")}</span>,
  },
  {
    title: "Arrival",
    key: "arrival",
    // The earliest check-in on that day's route — the figure the verdict beside it was read from.
    render: (_, x) => <span style={{ whiteSpace: "nowrap" }}>{fmtIST(x.arrivedAt, "HH:mm")}</span>,
  },
  {
    title: "Status",
    key: "status",
    render: (_, x) => <Tag color={STATUS_META[x.status].color}>{STATUS_META[x.status].label}</Tag>,
  },
];

// The punctuality columns, in whichever of the two shapes the filter calls for.
const arrivalColumns = (
  multiDay: boolean,
  onOpenDays: (d: DriverPerformance) => void
): ColumnsType<DriverPerformance> =>
  multiDay
    ? [
        {
          title: "Days on time",
          key: "days_on_time",
          width: 260,
          render: (_, d) => {
            const { onTime, judged } = dayTally(d);
            // No day we could judge — a dash, never "0 of 0", which would read as a driver who
            // was late every morning rather than one Loginext pushed no check-in for.
            if (judged === 0) {
              return (
                <Tooltip title="LogiNext pushed no check-in for this driver in this range, so none of their mornings can be judged.">
                  <span style={{ color: "#bbb", cursor: "help" }}>—</span>
                </Tooltip>
              );
            }
            return (
              <Tooltip title={`Each day this driver ran, judged against ${ARRIVAL_CUTOFF_LABEL} that morning. Click to see them.`}>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => onOpenDays(d)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") onOpenDays(d);
                  }}
                  style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                >
                  <Tag color={onTime === judged ? "green" : onTime === 0 ? "red" : "gold"}>
                    {onTime}
                  </Tag>
                  <span style={{ color: "#8c8c8c" }}>
                    of {judged} day{judged === 1 ? "" : "s"}
                  </span>
                </span>
              </Tooltip>
            );
          },
        },
      ]
    : [
        {
          title: "Arrival time",
          key: "arrival",
          width: 150,
          // LogiNext's earliest check-in on the route. fmtIST renders "—" when none was pushed.
          render: (_, d) => fmtIST(d.arrivedAt),
        },
        {
          title: "Status",
          key: "status",
          width: 110,
          render: (_, d) => {
            if (!d.status) return <span style={{ color: "#bbb" }}>—</span>;
            const meta = STATUS_META[d.status];
            return <Tag color={meta.color}>{meta.label}</Tag>;
          },
        },
      ];

const buildColumns = (
  multiDay: boolean,
  onOpenDays: (d: DriverPerformance) => void
): ColumnsType<DriverPerformance> => [
  {
    title: "Driver",
    key: "driver",
    width: 200,
    render: (_, d) => (
      <Tooltip title={d.trips.length ? `Trip ${d.trips.join(", ")}` : undefined}>
        <span>{d.driver}</span>
      </Tooltip>
    ),
  },
  {
    // The transporter the driver runs for — from the trip payload's vendor name, the same source
    // the Vendor filter above the page works on.
    title: "Vendor",
    key: "vendor",
    width: 180,
    render: (_, d) =>
      d.vendor ? <span>{d.vendor}</span> : <span style={{ color: "#bbb" }}>Unknown</span>,
  },
  {
    title: "Vehicle no.",
    key: "vehicle",
    width: 150,
    render: (_, d) =>
      d.vehicle ? (
        <span style={{ whiteSpace: "nowrap" }}>{d.vehicle}</span>
      ) : (
        <span style={{ color: "#bbb" }}>Not assigned</span>
      ),
  },
  ...arrivalColumns(multiDay, onOpenDays),
  {
    // PER SHIPMENT, unlike every other column here — this is asking how much of the load the driver
    // carried went late, so it has to count what they carried. The reason for any individual breach
    // is on that shipment's own row, under the Breach column on the Trips tab.
    title: "Breaches",
    key: "breaches",
    width: 170,
    render: (_, d) => {
      // Excused rides in the SAME cell rather than a column of its own: it is the remainder of this
      // driver's late drops, and reading it two columns away invites adding it to the breach count.
      const excused = d.excused > 0 ? (
        <Tooltip title={`A further ${d.excused} missed its cutoff on an escalation that is not the transporter's — not charged to this driver.`}>
          <Tag color="orange" style={{ marginInlineStart: 6, marginInlineEnd: 0, cursor: "help" }}>
            {d.excused} excused
          </Tag>
        </Tooltip>
      ) : null;
      return d.breaches > 0 ? (
        <span>
          <Tooltip title={`${d.breaches} of the ${d.shipments} shipment${d.shipments === 1 ? "" : "s"} this driver carried missed its delivery SLA.`}>
            <span style={{ cursor: "help" }}>
              <Tag color="red" style={{ marginInlineEnd: 6 }}>{d.breaches}</Tag>
              <span style={{ color: "#8c8c8c" }}>of {d.shipments}</span>
            </span>
          </Tooltip>
          {excused}
        </span>
      ) : (
        <span>
          <Tooltip title={`No shipment this driver carried is charged with a breach${d.excused > 0 ? "" : ` — all ${d.shipments} met their delivery SLA`}.`}>
            <span style={{ color: "#8c8c8c", cursor: "help" }}>0 of {d.shipments}</span>
          </Tooltip>
          {excused}
        </span>
      );
    },
  },
];

const DriverPerformanceDrawer: React.FC<Props> = ({ open, drivers, multiDay, onClose }) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  // The driver whose mornings are being read, in the panel beside this one. The house convention
  // for an overlay: one nullable row, never a boolean plus a payload that can disagree with it.
  const [daysFor, setDaysFor] = useState<DriverPerformance | null>(null);
  const columns = useMemo(() => buildColumns(multiDay, setDaysFor), [multiDay]);

  useEffect(() => {
    if (open) setPage(1);
  }, [open]);

  // Back to one day, or the drawer closed: the day panel is about a range and has nothing to say.
  useEffect(() => {
    if (!open || !multiDay) setDaysFor(null);
  }, [open, multiDay]);

  // The driver rows are rebuilt by every reload, so hold the NAME and re-read the fresh row — an
  // open panel then follows the data instead of showing a tally the table behind it no longer has.
  const daysDriver = daysFor ? drivers.find((d) => d.driver === daysFor.driver) ?? null : null;

  const pageRows = drivers.slice((page - 1) * pageSize, page * pageSize);

  return (
    <Drawer
      title={`Driver performance — ${drivers.length} driver${drivers.length === 1 ? "" : "s"}`}
      width={900}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      <div style={{ marginBottom: 12, color: "#6b7280", fontSize: 12 }}>
        Arrival before {ARRIVAL_CUTOFF_LABEL} counts as on time. Drivers with breaches are listed
        first; a shipment excused by an escalation is shown apart and not charged to the driver.
        {ARRIVAL_DATA_AVAILABLE
          ? multiDay
            ? " Arrival is the earliest LogiNext check-in on the driver's route that day; over this range each day is judged on its own and counted."
            : " Arrival is the earliest LogiNext check-in across the driver's route."
          : " Arrival times are not wired to a source yet."}
      </div>
      <Table
        rowKey="driver"
        size="small"
        columns={columns}
        dataSource={pageRows}
        pagination={false}
        scroll={{ x: 920 }}
      />
      <div style={{ marginTop: 16, textAlign: "right" }}>
        <Pagination
          current={page}
          pageSize={pageSize}
          total={drivers.length}
          showSizeChanger
          pageSizeOptions={["20", "50", "100", "200"]}
          showTotal={(t) => `${t} driver${t === 1 ? "" : "s"}`}
          onChange={(p, size) => {
            setPage(p);
            setPageSize(size);
          }}
        />
      </div>

      {/* One driver's mornings. Nested inside this drawer, so the table it was opened from stays on
          screen behind it and closing comes back to the same scroll position. */}
      <Drawer
        title={daysDriver ? `${daysDriver.driver} — ${dayTally(daysDriver).judged} day${dayTally(daysDriver).judged === 1 ? "" : "s"}` : ""}
        width="min(420px, 90vw)"
        open={!!daysDriver}
        onClose={() => setDaysFor(null)}
        destroyOnClose
      >
        {daysDriver && (
          <>
            <div style={{ marginBottom: 12, color: "#6b7280", fontSize: 12 }}>
              {daysDriver.vendor ? `${daysDriver.vendor} · ` : ""}
              {daysDriver.vehicle ?? "No vehicle"} — earliest LogiNext check-in on each day this
              driver ran, judged against {ARRIVAL_CUTOFF_LABEL} that morning.{" "}
              <strong>{dayTally(daysDriver).onTime}</strong> of {dayTally(daysDriver).judged} on
              time. Days LogiNext pushed no check-in for are not listed and are not counted.
            </div>
            <Table
              rowKey="day"
              size="small"
              columns={dayColumns}
              dataSource={daysDriver.days}
              pagination={false}
            />
          </>
        )}
      </Drawer>
    </Drawer>
  );
};

export default DriverPerformanceDrawer;
